import { useState } from 'react';
import { Check, Link2, Mail, MessageCircle, Share2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import { t } from '@/lib/i18n';

/*
 * Brand marks, inline.
 *
 * Drawn here rather than pulled from an icon pack because the pack's "Twitter"
 * is still a bird, and because a share sheet that loads logos from a CDN is a
 * share sheet that shows empty boxes under our own content-security policy.
 */
function Facebook({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
      <path d="M24 12.07C24 5.4 18.63 0 12 0S0 5.4 0 12.07C0 18.1 4.39 23.1 10.13 24v-8.44H7.08v-3.49h3.05V9.41c0-3.02 1.79-4.69 4.53-4.69 1.31 0 2.68.24 2.68.24v2.97h-1.51c-1.49 0-1.96.93-1.96 1.89v2.25h3.33l-.53 3.49h-2.8V24C19.61 23.1 24 18.1 24 12.07Z" />
    </svg>
  );
}

function X({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24h-6.66l-5.214-6.817-5.966 6.817H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231 5.45-6.231Zm-1.161 17.52h1.833L7.084 4.126H5.117l11.966 15.644Z" />
    </svg>
  );
}

function WhatsApp({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
      <path d="M.057 24l1.687-6.163a11.867 11.867 0 0 1-1.587-5.945C.16 5.335 5.495 0 12.05 0a11.817 11.817 0 0 1 8.413 3.488 11.824 11.824 0 0 1 3.48 8.414c-.003 6.557-5.338 11.892-11.893 11.892a11.9 11.9 0 0 1-5.688-1.448L.057 24Zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884a9.86 9.86 0 0 0 1.599 5.328l-.999 3.648 3.889-1.02Zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.148-.669.149-.198.297-.767.967-.94 1.165-.174.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.52.149-.174.198-.298.297-.497.1-.198.05-.371-.025-.52-.074-.149-.668-1.612-.916-2.207-.241-.579-.486-.5-.668-.51l-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.872.118.57-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413Z" />
    </svg>
  );
}

function Telegram({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
      <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0Zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.911.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635Z" />
    </svg>
  );
}

function LinkedIn({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286ZM5.337 7.433a2.062 2.062 0 0 1-2.063-2.065 2.064 2.064 0 1 1 2.063 2.065Zm1.782 13.019H3.555V9h3.564v11.452ZM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.225 0Z" />
    </svg>
  );
}

export interface ShareTarget {
  url: string;
  title: string;
  /** Usually the author's name — what a reader would say when passing it on. */
  byline?: string;
}

type Channel = {
  key: string;
  label: string;
  icon: React.ReactNode;
  /** Built from the already-encoded pieces. */
  href: (encoded: { url: string; title: string; text: string }) => string;
  /** App-scheme links do nothing without the app; keep them off desktop. */
  appOnly?: boolean;
};

/*
 * Ordered for this audience, not for a generic Western one: in Nepal a poem
 * travels on Facebook and Viber far more than on X.
 */
const CHANNELS: Channel[] = [
  {
    key: 'facebook',
    label: t('फेसबुक'),
    icon: <Facebook className="size-4 text-[#1877F2]" />,
    href: ({ url }) => `https://www.facebook.com/sharer/sharer.php?u=${url}`,
  },
  {
    key: 'viber',
    label: t('भाइबर'),
    icon: <MessageCircle className="size-4 text-[#7360F2]" />,
    href: ({ text }) => `viber://forward?text=${text}`,
    appOnly: true,
  },
  {
    key: 'whatsapp',
    label: t('ह्वाट्सएप'),
    icon: <WhatsApp className="size-4 text-[#25D366]" />,
    href: ({ text }) => `https://wa.me/?text=${text}`,
  },
  {
    key: 'x',
    label: t('एक्स'),
    icon: <X className="size-4" />,
    href: ({ url, title }) => `https://x.com/intent/post?url=${url}&text=${title}`,
  },
  {
    key: 'telegram',
    label: t('टेलिग्राम'),
    icon: <Telegram className="size-4 text-[#26A5E4]" />,
    href: ({ url, title }) => `https://t.me/share/url?url=${url}&text=${title}`,
  },
  {
    key: 'linkedin',
    label: t('लिंक्डइन'),
    icon: <LinkedIn className="size-4 text-[#0A66C2]" />,
    href: ({ url }) => `https://www.linkedin.com/sharing/share-offsite/?url=${url}`,
  },
  {
    key: 'email',
    label: t('इमेल'),
    icon: <Mail className="size-4 text-muted-foreground" />,
    href: ({ title, text }) => `mailto:?subject=${title}&body=${text}`,
  },
];

/**
 * Share sheet for a post or an author's site.
 *
 * The URL passed in should be the canonical one. A piece is readable both at
 * kalampress.com/author/<handle>/<slug> and at <handle>.kalampress.com/p/<slug>,
 * and sharing whichever address the reader happens to be on would split one
 * poem's shares and comments across two Facebook objects.
 */
export function ShareMenu({
  target,
  variant = 'outline',
  iconOnly = false,
  className,
}: {
  target: ShareTarget;
  variant?: 'outline' | 'secondary' | 'ghost';
  iconOnly?: boolean;
  className?: string;
}) {
  const [copied, setCopied] = useState(false);

  const message = target.byline ? `“${target.title}” — ${target.byline}` : target.title;
  const encoded = {
    url: encodeURIComponent(target.url),
    title: encodeURIComponent(message),
    text: encodeURIComponent(`${message}\n${target.url}`),
  };

  /*
   * Popup rather than a new tab: the sharer dialogs are sized for it, and it
   * leaves the reader's place in the piece untouched behind the window.
   */
  const open = (href: string) => {
    window.open(href, '_blank', 'noopener,noreferrer,width=620,height=680');
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(target.url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard access is refused outside a secure context and in some
      // in-app browsers. Prompting is uglier than a silent failure, but it
      // still lets the reader get the link out.
      window.prompt(t('लिंक कपी गर्नुहोस्'), target.url);
    }
  };

  /* Coarse pointer stands in for "has messaging apps installed". */
  const touch = typeof window !== 'undefined' && window.matchMedia?.('(pointer: coarse)').matches;
  const channels = CHANNELS.filter((channel) => !channel.appOnly || touch);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant={variant}
          size={iconOnly ? 'icon' : 'sm'}
          className={className}
          aria-label={t('साझा गर्नुहोस्')}
          title={t('साझा गर्नुहोस्')}
        >
          <Share2 />
          {!iconOnly && <span className="np">{t('साझा गर्नुहोस्')}</span>}
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end" className="w-52">
        {channels.map((channel) => (
          <DropdownMenuItem
            key={channel.key}
            onSelect={() => open(channel.href(encoded))}
            className="gap-2.5"
          >
            {channel.icon}
            <span className="np">{channel.label}</span>
          </DropdownMenuItem>
        ))}

        <DropdownMenuSeparator />

        <DropdownMenuItem onSelect={(event) => { event.preventDefault(); void copy(); }} className="gap-2.5">
          {copied ? <Check className="size-4 text-primary" /> : <Link2 className="size-4 text-muted-foreground" />}
          <span className={cn('np', copied && 'text-primary')}>
            {copied ? t('लिंक कपी भयो') : t('लिंक कपी गर्नुहोस्')}
          </span>
        </DropdownMenuItem>

        {typeof navigator !== 'undefined' && 'share' in navigator && (
          <DropdownMenuItem
            onSelect={() => {
              void navigator.share({ title: target.title, text: message, url: target.url }).catch(() => undefined);
            }}
            className="gap-2.5"
          >
            <Share2 className="size-4 text-muted-foreground" />
            <span className="np">{t('थप विकल्प…')}</span>
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
